import React, {useState, useEffect, useRef, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import {AdminMeeting, AdminUser, AccountRef, MeetingStats, MEETING_TYPE_LABELS} from './core/_models'
import {getMeetingStats, getTodayMeetings, getAdminUsers, claimMeeting, updateMeeting, rescheduleMeeting, switchToAccount} from './core/_requests'
import {getAdminToken} from '../auth/_requests'
import moment from 'moment'
import 'moment/locale/he'

const POLL_INTERVAL_NORMAL = 60000 // 60 seconds
const POLL_INTERVAL_URGENT = 15000 // 15 seconds

type MeetingUrgency = 'none' | 'pulse' | 'aggressive' | 'overdue'

function getMeetingUrgency(meeting: AdminMeeting): MeetingUrgency {
  if (meeting.claimed_by) return 'none'
  const now = moment()
  const meetingTime = moment(meeting.meeting_date)
  const minBefore = meetingTime.diff(now, 'minutes', true)
  const minAfter = now.diff(meetingTime, 'minutes', true)

  // >15 min past → overdue (no flash, show actions)
  if (minAfter > 15) return 'overdue'
  // 5 min before → 15 min after → aggressive
  if (minBefore <= 5 && minAfter <= 15) return 'aggressive'
  // 10 min before → 5 min before → pulse
  if (minBefore <= 10 && minBefore > 5) return 'pulse'
  return 'none'
}

const MeetingsBubble: React.FC = () => {
  const navigate = useNavigate()
  const [stats, setStats] = useState<MeetingStats | null>(null)
  const [todayMeetings, setTodayMeetings] = useState<AdminMeeting[]>([])
  const [showPanel, setShowPanel] = useState(false)
  const [ticketCount, setTicketCount] = useState(0)
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [claimSelections, setClaimSelections] = useState<Record<string, string>>({})
  const [claimingId, setClaimingId] = useState<string | null>(null)
  const [rescheduleId, setRescheduleId] = useState<string | null>(null)
  const [rescheduleDate, setRescheduleDate] = useState('')
  const notifiedMeetingIds = useRef<Set<string>>(new Set())

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, todayRes] = await Promise.all([
        getMeetingStats(),
        getTodayMeetings(),
      ])
      setStats(statsRes.data)
      setTodayMeetings(todayRes.data.meetings)

      // Browser notifications for imminent meetings
      if (statsRes.data.within30min > 0 && Notification.permission === 'granted') {
        const now = moment()
        todayRes.data.meetings.forEach((meeting: AdminMeeting) => {
          const meetingTime = moment(meeting.meeting_date)
          const diffMin = meetingTime.diff(now, 'minutes')
          if (diffMin >= 0 && diffMin <= 30 && !notifiedMeetingIds.current.has(meeting._id)) {
            notifiedMeetingIds.current.add(meeting._id)
            const acc = meeting.account as AccountRef
            const name = acc ? `${acc.first_name || ''} ${acc.last_name || ''}`.trim() : ''
            new Notification('', {
              body: ` ${name}  ${diffMin} !`,
              tag: `meeting-${meeting._id}`,
              icon: '/favicon.ico',
            })
          }
        })
      }
    } catch (e) {
      // Silently fail
    }
  }, [])

  const fetchTicketStats = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL
      const token = getAdminToken()
      const headers: HeadersInit = token ? {Authorization: `Bearer ${token}`} : {}
      const response = await fetch(`${API_URL}/admin/tickets/stats`, {
        credentials: 'include',
        headers,
      })
      if (response.ok) {
        const data = await response.json()
        setTicketCount((data.totalOpen || 0) + (data.totalUnassigned || 0))
      }
    } catch (e) {
      // Silently fail
    }
  }, [])

  // Fetch admin users when panel opens
  useEffect(() => {
    if (showPanel && adminUsers.length === 0) {
      getAdminUsers().then((res) => setAdminUsers(res.data.admins)).catch(() => {})
    }
  }, [showPanel, adminUsers.length])

  // Dynamic poll interval based on urgency
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }

    fetchData()
    fetchTicketStats()

    const interval = setInterval(() => {
      fetchData()
      fetchTicketStats()
    }, stats?.needsFlash ? POLL_INTERVAL_URGENT : POLL_INTERVAL_NORMAL)

    return () => clearInterval(interval)
  }, [fetchData, fetchTicketStats, stats?.needsFlash])

  // Manage page flash body class (whole page flashes red)
  useEffect(() => {
    if (stats?.needsFlash) {
      document.body.classList.add('meeting-page-flash-active')
    } else {
      document.body.classList.remove('meeting-page-flash-active')
    }
    if (stats?.needsHeaderFlash) {
      document.body.classList.add('meeting-page-flash-hard')
    } else {
      document.body.classList.remove('meeting-page-flash-hard')
    }
    return () => {
      document.body.classList.remove('meeting-page-flash-active')
      document.body.classList.remove('meeting-page-flash-hard')
    }
  }, [stats?.needsFlash, stats?.needsHeaderFlash])

  const getBubbleColor = () => {
    if (!stats) return '#1bc5bd'
    switch (stats.bubbleColor) {
      case 'red':
        return '#f64e60'
      case 'yellow':
        return '#ffa800'
      default:
        return '#1bc5bd'
    }
  }

  const getTicketBubbleColor = () => {
    if (ticketCount > 10) return '#f64e60'
    if (ticketCount > 5) return '#ffa800'
    return '#3699ff'
  }

  const getBubbleAnimation = () => {
    if (!stats) return 'none'
    if (stats.needsHeaderFlash) return 'urgentPulse 0.6s ease-in-out infinite'
    if (stats.needsFlash) return 'urgentPulse 1.5s ease-in-out infinite'
    if (stats.bubbleColor === 'red') return 'pulse 2s ease-in-out infinite'
    return 'none'
  }

  const handleClaim = async (meetingId: string) => {
    const adminId = claimSelections[meetingId]
    if (!adminId) return
    setClaimingId(meetingId)
    try {
      await claimMeeting(meetingId, adminId)
      await fetchData()
      setClaimSelections((prev) => {
        const next = {...prev}
        delete next[meetingId]
        return next
      })
    } catch (e) {
      // Silently fail
    }
    setClaimingId(null)
  }

  const handleUnclaim = async (meetingId: string) => {
    try {
      await updateMeeting(meetingId, {claimed_by: null} as any)
      await fetchData()
    } catch (e) {
      // Silently fail
    }
  }

  const handleComplete = async (meetingId: string) => {
    try {
      await updateMeeting(meetingId, {status: 'completed'})
      await fetchData()
    } catch (e) {
      // Silently fail
    }
  }

  const handleReschedule = async (meetingId: string) => {
    if (!rescheduleDate) return
    try {
      await rescheduleMeeting(meetingId, rescheduleDate)
      setRescheduleId(null)
      setRescheduleDate('')
      await fetchData()
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה בדחיית הפגישה')
    }
  }

  // Compute worst urgency from unclaimed urgent meetings for the floating banner
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!stats?.needsFlash) return
    // Re-render every 10s to update "passed X min" text
    const t = setInterval(() => setTick((n) => n + 1), 10000)
    return () => clearInterval(t)
  }, [stats?.needsFlash])

  const getFloatingBannerInfo = () => {
    if (!stats?.unclaimedUrgent?.length) return null
    const now = moment()
    // Find the most urgent (closest / most overdue) meeting
    let worstMeeting = stats.unclaimedUrgent[0]
    let worstMinPast = -Infinity
    for (const m of stats.unclaimedUrgent) {
      const minPast = now.diff(moment(m.meeting_date), 'minutes', true)
      if (minPast > worstMinPast) {
        worstMinPast = minPast
        worstMeeting = m
      }
    }
    const meetingTime = moment(worstMeeting.meeting_date)
    const isPast = now.isAfter(meetingTime)
    const minPast = Math.floor(now.diff(meetingTime, 'minutes', true))
    const minBefore = Math.floor(meetingTime.diff(now, 'minutes', true))
    return { isPast, minPast, minBefore, meetingTime }
  }

  const bannerInfo = stats?.needsFlash ? getFloatingBannerInfo() : null

  // Determine which meetings to show in panel: today + unclaimed overdue
  const meetingsToShow = todayMeetings

  return (
    <>
      {/* Meetings Bubble */}
      <div
        onClick={() => {
          setShowPanel(!showPanel)
        }}
        style={{
          position: 'fixed',
          bottom: '24px',
          left: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: getBubbleColor(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          zIndex: 1000,
          transition: 'background 0.3s ease',
          animation: getBubbleAnimation(),
        }}
      >
        <i className='bi bi-calendar-event' style={{fontSize: '24px', color: '#fff'}}></i>
        {stats && stats.todayRemaining > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              background: '#fff',
              color: getBubbleColor(),
              fontSize: '12px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}
          >
            {stats.todayRemaining}
          </div>
        )}
      </div>

      {/* Flashing arrow pointing to bubble when meeting < 10 min away */}
      {stats?.needsFlash && !showPanel && bannerInfo && (
        <>
          {/* Full page red flash overlay */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 998,
              pointerEvents: 'none',
              animation: bannerInfo.isPast
                ? 'fullPageFlashHard 0.5s ease-in-out infinite'
                : 'fullPageFlash 1.2s ease-in-out infinite',
            }}
          />
          {/* Bouncing banner + arrow */}
          <div
            style={{
              position: 'fixed',
              bottom: '88px',
              left: '14px',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              animation: bannerInfo.isPast
                ? 'bubbleArrowBounce 0.35s ease-in-out infinite'
                : 'bubbleArrowBounce 0.6s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                background: bannerInfo.isPast ? '#d9214e' : '#f64e60',
                color: '#fff',
                padding: bannerInfo.isPast ? '10px 22px' : '8px 16px',
                borderRadius: '10px',
                fontSize: bannerInfo.isPast ? '18px' : '15px',
                fontWeight: 800,
                whiteSpace: 'nowrap',
                boxShadow: bannerInfo.isPast
                  ? '0 6px 32px rgba(217, 33, 78, 0.8)'
                  : '0 4px 24px rgba(246, 78, 96, 0.6)',
                marginBottom: '6px',
                animation: bannerInfo.isPast
                  ? 'badgeFlashHard 0.3s ease-in-out infinite alternate'
                  : 'badgeFlash 0.5s ease-in-out infinite alternate',
              }}
            >
              {bannerInfo.isPast ? (
                <>
                  <i className='bi bi-exclamation-triangle-fill' style={{marginLeft: '6px'}}></i>
                  פגישה עברה לפני {bannerInfo.minPast} דקות!
                  <i className='bi bi-exclamation-triangle-fill' style={{marginRight: '6px'}}></i>
                </>
              ) : (
                <>
                  <i className='bi bi-exclamation-triangle-fill' style={{marginLeft: '6px'}}></i>
                  פגישה בעוד {bannerInfo.minBefore} דקות!
                  <i className='bi bi-exclamation-triangle-fill' style={{marginRight: '6px'}}></i>
                </>
              )}
            </div>
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: bannerInfo.isPast ? '14px solid transparent' : '12px solid transparent',
                borderRight: bannerInfo.isPast ? '14px solid transparent' : '12px solid transparent',
                borderTop: bannerInfo.isPast ? '16px solid #d9214e' : '14px solid #f64e60',
                animation: bannerInfo.isPast
                  ? 'badgeFlashHard 0.3s ease-in-out infinite alternate'
                  : 'badgeFlash 0.5s ease-in-out infinite alternate',
              }}
            />
          </div>
        </>
      )}

      {/* Tickets Bubble */}
      <div
        onClick={() => {
          setShowPanel(false)
          navigate('/admin/tickets?status=active')
        }}
        style={{
          position: 'fixed',
          bottom: '92px',
          left: '24px',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: getTicketBubbleColor(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          zIndex: 1000,
          transition: 'all 0.3s ease',
          animation: ticketCount > 10 ? 'pulse 2s ease-in-out infinite' : 'none',
        }}
      >
        <i className='bi bi-envelope-exclamation' style={{fontSize: '20px', color: '#fff'}}></i>
        {ticketCount > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '22px',
              height: '22px',
              borderRadius: '11px',
              background: '#fff',
              color: getTicketBubbleColor(),
              fontSize: '11px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}
          >
            {ticketCount}
          </div>
        )}
      </div>

      {/* Meetings Panel */}
      {showPanel && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 999,
            }}
            onClick={() => setShowPanel(false)}
          />
          <div
            style={{
              position: 'fixed',
              bottom: '90px',
              left: '24px',
              width: '380px',
              maxHeight: '480px',
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              zIndex: 1001,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Panel Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{fontWeight: 600, fontSize: '16px', color: '#1e1e2d'}}>
                <i className='bi bi-calendar-event' style={{marginLeft: '8px', color: getBubbleColor()}}></i>
                פגישות היום
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span
                  style={{
                    background: getBubbleColor(),
                    color: '#fff',
                    padding: '2px 10px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {stats?.todayRemaining || 0} נותרו
                </span>
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowPanel(false)
                    navigate('/admin/meetings?create=true')
                  }}
                  title='פגישה חדשה'
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: '#3699ff',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#2d80db')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#3699ff')}
                >
                  <i className='bi bi-plus-lg'></i>
                </div>
              </div>
            </div>

            {/* Meetings List */}
            <div style={{flex: 1, overflowY: 'auto', padding: '8px 0'}}>
              {meetingsToShow.length === 0 ? (
                <div style={{padding: '32px 20px', textAlign: 'center', color: '#a1a5b7'}}>
                  <i className='bi bi-calendar-check' style={{fontSize: '32px', display: 'block', marginBottom: '8px'}}></i>
                  אין פגישות להיום
                </div>
              ) : (
                meetingsToShow.map((meeting) => {
                  const acc = meeting.account as AccountRef
                  const name = acc ? `${acc.first_name || ''} ${acc.last_name || ''}`.trim() : '\u2014'
                  const meetingTime = moment(meeting.meeting_date)
                  const isPast = meetingTime.isBefore(moment())
                  const urgency = getMeetingUrgency(meeting)
                  const isClaimed = !!meeting.claimed_by
                  const claimedAdmin = meeting.claimed_by && typeof meeting.claimed_by === 'object'
                    ? (meeting.claimed_by as AdminUser).username
                    : null

                  const getRowBackground = () => {
                    if (isClaimed) return 'rgba(27, 197, 189, 0.06)'
                    if (urgency === 'aggressive' || urgency === 'pulse') return 'rgba(246, 78, 96, 0.08)'
                    if (urgency === 'overdue') return 'rgba(161, 165, 183, 0.1)'
                    return 'transparent'
                  }

                  const getRowBorder = () => {
                    if (urgency === 'aggressive') return '3px solid #f64e60'
                    if (urgency === 'pulse') return '3px solid #ffa800'
                    if (isClaimed) return '3px solid #1bc5bd'
                    return '3px solid transparent'
                  }

                  return (
                    <div
                      key={meeting._id}
                      style={{
                        padding: '12px 20px',
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                        background: getRowBackground(),
                        borderRight: getRowBorder(),
                        animation: urgency === 'aggressive' ? 'rowFlash 0.8s ease-in-out infinite' : 'none',
                      }}
                    >
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <div style={{fontWeight: 600, fontSize: '14px', color: '#1e1e2d'}}>{name}</div>
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: urgency === 'aggressive' || urgency === 'pulse' ? '#f64e60' : isPast ? '#a1a5b7' : '#1e1e2d',
                          }}
                        >
                          {meetingTime.format('HH:mm')}
                        </div>
                      </div>
                      <div style={{fontSize: '12px', color: '#5e6278', marginTop: '4px'}}>
                        {meeting.title}
                        <span
                          style={{
                            marginRight: '8px',
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            background: 'rgba(54, 153, 255, 0.1)',
                            color: '#3699ff',
                          }}
                        >
                          {MEETING_TYPE_LABELS[meeting.meeting_type]}
                        </span>
                      </div>

                      {/* Phone + Login row */}
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px'}}>
                        {acc?.phone && (
                          <a
                            href={`tel:${acc.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              color: '#1bc5bd',
                              fontWeight: 600,
                              textDecoration: 'none',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background: 'rgba(27, 197, 189, 0.1)',
                              transition: 'background 0.15s ease',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(27, 197, 189, 0.2)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(27, 197, 189, 0.1)')}
                          >
                            <i className='bi bi-telephone-fill' style={{fontSize: '10px'}}></i>
                            {acc.phone}
                          </a>
                        )}
                        {acc?._id && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation()
                              try {
                                const res = await switchToAccount(acc._id)
                                const loginUrl = `${window.location.origin}/?overide-token=${res.data.token}`
                                window.location.href = loginUrl
                              } catch (err) {
                                console.error('Failed to switch account:', err)
                              }
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '11px',
                              color: '#3699ff',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: '1px solid rgba(54, 153, 255, 0.3)',
                              background: 'rgba(54, 153, 255, 0.06)',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#3699ff'
                              e.currentTarget.style.color = '#fff'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(54, 153, 255, 0.06)'
                              e.currentTarget.style.color = '#3699ff'
                            }}
                          >
                            <i className='bi bi-box-arrow-in-left' style={{fontSize: '10px'}}></i>
                            כניסה לחשבון
                          </button>
                        )}
                      </div>

                      {/* Claimed badge */}
                      {isClaimed && claimedAdmin && (
                        <div style={{fontSize: '11px', color: '#1bc5bd', fontWeight: 600, marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                          <span>
                            <i className='bi bi-check-circle-fill' style={{marginLeft: '4px'}}></i>
                            נתפס ע"י {claimedAdmin}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleUnclaim(meeting._id)
                            }}
                            style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: '1px solid #e4e6ef',
                              background: '#fff',
                              color: '#a1a5b7',
                              fontSize: '10px',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f64e60'
                              e.currentTarget.style.color = '#fff'
                              e.currentTarget.style.borderColor = '#f64e60'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#fff'
                              e.currentTarget.style.color = '#a1a5b7'
                              e.currentTarget.style.borderColor = '#e4e6ef'
                            }}
                          >
                            שחרר
                          </button>
                        </div>
                      )}

                      {/* Urgency indicator */}
                      {!isClaimed && (urgency === 'pulse' || urgency === 'aggressive') && (
                        <div style={{fontSize: '11px', color: '#f64e60', fontWeight: 600, marginTop: '4px'}}>
                          <i className='bi bi-exclamation-triangle-fill' style={{marginLeft: '4px'}}></i>
                          {isPast
                            ? `התחילה לפני ${Math.abs(meetingTime.diff(moment(), 'minutes'))} דקות!`
                            : `בעוד ${Math.max(0, meetingTime.diff(moment(), 'minutes'))} דקות!`
                          }
                        </div>
                      )}

                      {/* Claim UI: unclaimed + within urgency window */}
                      {!isClaimed && (urgency === 'pulse' || urgency === 'aggressive') && (
                        <div style={{marginTop: '8px', display: 'flex', gap: '6px', alignItems: 'center'}}>
                          <select
                            value={claimSelections[meeting._id] || ''}
                            onChange={(e) =>
                              setClaimSelections((prev) => ({...prev, [meeting._id]: e.target.value}))
                            }
                            style={{
                              flex: 1,
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: '1px solid #e4e6ef',
                              fontSize: '12px',
                              background: '#fff',
                            }}
                          >
                            <option value=''>בחר מי מטפל...</option>
                            {adminUsers.map((admin) => (
                              <option key={admin._id} value={admin._id}>
                                {admin.username}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleClaim(meeting._id)}
                            disabled={!claimSelections[meeting._id] || claimingId === meeting._id}
                            style={{
                              padding: '4px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              background: claimSelections[meeting._id] ? '#f64e60' : '#e4e6ef',
                              color: '#fff',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: claimSelections[meeting._id] ? 'pointer' : 'not-allowed',
                              opacity: claimingId === meeting._id ? 0.6 : 1,
                            }}
                          >
                            {claimingId === meeting._id ? '...' : 'בחר'}
                          </button>
                        </div>
                      )}

                      {/* Overdue actions: unclaimed + >15 min past */}
                      {!isClaimed && urgency === 'overdue' && (
                        <div style={{marginTop: '8px'}}>
                          <div style={{fontSize: '11px', color: '#a1a5b7', marginBottom: '6px'}}>
                            <i className='bi bi-clock-history' style={{marginLeft: '4px'}}></i>
                            הפגישה עברה
                          </div>
                          <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                            <button
                              onClick={() => handleComplete(meeting._id)}
                              style={{
                                padding: '4px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                background: '#1bc5bd',
                                color: '#fff',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              הושלם
                            </button>
                            {rescheduleId === meeting._id ? (
                              <div style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
                                <input
                                  type='datetime-local'
                                  value={rescheduleDate}
                                  onChange={(e) => setRescheduleDate(e.target.value)}
                                  style={{
                                    padding: '4px 6px',
                                    borderRadius: '6px',
                                    border: '1px solid #e4e6ef',
                                    fontSize: '11px',
                                  }}
                                />
                                <button
                                  onClick={() => handleReschedule(meeting._id)}
                                  disabled={!rescheduleDate}
                                  style={{
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: rescheduleDate ? '#3699ff' : '#e4e6ef',
                                    color: '#fff',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    cursor: rescheduleDate ? 'pointer' : 'not-allowed',
                                  }}
                                >
                                  אישור
                                </button>
                                <button
                                  onClick={() => {
                                    setRescheduleId(null)
                                    setRescheduleDate('')
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #e4e6ef',
                                    background: '#fff',
                                    color: '#5e6278',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ביטול
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setRescheduleId(meeting._id)}
                                style={{
                                  padding: '4px 12px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  background: '#3699ff',
                                  color: '#fff',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                              >
                                תזמן מחדש
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>

            {/* Panel Footer */}
            <div
              onClick={() => {
                setShowPanel(false)
                navigate('/admin/meetings')
              }}
              style={{
                padding: '12px 20px',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                textAlign: 'center',
                cursor: 'pointer',
                color: '#3699ff',
                fontWeight: 500,
                fontSize: '13px',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(54, 153, 255, 0.06)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              צפה בכל הפגישות
              <i className='bi bi-arrow-left' style={{marginRight: '6px'}}></i>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 4px 16px rgba(0,0,0,0.2); }
          50% { box-shadow: 0 4px 24px rgba(246, 78, 96, 0.5); }
          100% { box-shadow: 0 4px 16px rgba(0,0,0,0.2); }
        }
        @keyframes urgentPulse {
          0%, 100% { box-shadow: 0 4px 16px rgba(0,0,0,0.2); transform: scale(1); }
          50% { box-shadow: 0 4px 32px rgba(246, 78, 96, 0.7); transform: scale(1.08); }
        }
        @keyframes rowFlash {
          0%, 100% { background-color: rgba(246, 78, 96, 0.08); }
          50% { background-color: rgba(246, 78, 96, 0.2); }
        }
        @keyframes headerFlash {
          0%, 100% { background-color: transparent; }
          50% { background-color: rgba(246, 78, 96, 0.12); }
        }
        @keyframes bubbleArrowBounce {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(-10px); opacity: 0.8; }
        }
        @keyframes badgeFlash {
          0% { transform: scale(1); box-shadow: 0 4px 24px rgba(246, 78, 96, 0.6); }
          100% { transform: scale(1.08); box-shadow: 0 4px 32px rgba(246, 78, 96, 0.9); }
        }
        @keyframes badgeFlashHard {
          0% { transform: scale(1); box-shadow: 0 6px 32px rgba(217, 33, 78, 0.7); }
          100% { transform: scale(1.12); box-shadow: 0 6px 40px rgba(217, 33, 78, 1); }
        }
        @keyframes screenBorderFlash {
          0%, 100% { border-color: transparent; }
          50% { border-color: rgba(246, 78, 96, 0.5); }
        }
        @keyframes screenBorderFlashHard {
          0%, 100% { border-color: transparent; }
          50% { border-color: rgba(217, 33, 78, 0.8); }
        }
        @keyframes fullPageFlash {
          0%, 100% { background-color: transparent; }
          50% { background-color: rgba(246, 78, 96, 0.08); }
        }
        @keyframes fullPageFlashHard {
          0%, 100% { background-color: transparent; }
          50% { background-color: rgba(217, 33, 78, 0.15); }
        }
      `}</style>
    </>
  )
}

export default MeetingsBubble
